import React, {useCallback, useContext, useEffect, useState} from 'react';
import {Redirect, Route} from 'react-router-dom';
import AppContext from '../context/AppContext';
import authService from "../services/authService";
import Loading from "../components/util/Loading";
import {useMediaQuery} from "react-responsive";
import {device} from "../lib/breakpoints";
import LocalTokenInput from "../components/local/LocalTokenInput";


const ProtectedRoute = ({component: Component, ...rest}) => {
    const {auth, appStore} = useContext(AppContext);
    const isLaptop = useMediaQuery({query: device.laptop});
    const [isLoading, setIsLoading] = useState(true);
    const [showLocalTokenModal, setShowLocalTokenModal] = useState(false);

    const internalUserDefaultRoute = '/profile'
    const internalUserRestrictedRoutes = ['']

    const {setReturnUrl, redirectToTarget, isRedirect, internalRedirect, redirectObj} = {...rest};
    setReturnUrl && setReturnUrl(window.location.href);

    const forceToInternalUserPage = () => {
        const {path} = {...rest}
        const isUserLoggedIn = auth.user;
        if (isUserLoggedIn) {
            const isExternalUser = authService.isExternalUser(auth.user.username);
            return internalUserRestrictedRoutes.indexOf(path) > -1 && !isExternalUser
        }
        return false;
    }

    const attemptTokenRetrieval = useCallback(async () => {
        if (!auth.user) {
            if (authService.isLocalhost() && authService.hasLocalToken()) {
                const localToken = authService.getLocalToken();
                auth.loginWithToken(localToken);
                setIsLoading(false);
                return;
            }
            try {
                console.log("Attempting to get token from session cookie");
                const response = await authService.getTokenFromSessionCookie();
                if (response?.data?.jwtToken) {
                    console.log("JWT token found, logging in user");
                    auth.loginWithToken(response.data.jwtToken);
                }
            } catch (error) {
                console.error("Error in getTokenFromSessionCookie:", error);
                if (authService.isLocalhost() && !authService.hasLocalToken()) {
                    console.log("On localhost without session and no token - showing local token modal");
                    setShowLocalTokenModal(true);
                }
            }
        }
        setIsLoading(false);
    }, [auth]);

    useEffect(() => {
        let mounted = true;

        const initAuth = async () => {
            await attemptTokenRetrieval();
            if (!mounted) return;
        };

        initAuth();

        return () => {
            mounted = false;
        };
    }, [attemptTokenRetrieval]);

    const handleDevTokenSubmit = (token) => {
        authService.setDevToken(token);
        if (token) {
            auth.loginWithToken(token);
        }
    };

    return (
        <>
            {showLocalTokenModal && (
                <LocalTokenInput
                    onTokenSubmit={handleDevTokenSubmit}
                    currentToken={authService.getLocalToken()}
                />
            )}
            <Route
                {...rest}
                render={props => {
                    if (isLoading) {
                        return <Loading/>;
                    }

                // Handle active tab synchronously
                if (props.location.activeTab) {
                    if (isLaptop) {
                        appStore.setProfileTabPath(props.location.activeTab.desk);
                    } else {
                        appStore.setProfileTabPath(props.location.activeTab.mobile);
                        appStore.setComponentPathInfo(props.location.activeTab.mobile);
                        appStore.setShowPanel('show')
                        appStore.setIsMobileNonHeaderView(true);
                    }
                    props.location.activeTab = null;
                }

                // Authentication and routing logic
                if (auth.user) {
                    props.location.search = null;

                    if (forceToInternalUserPage()) {
                        return <Redirect to={internalUserDefaultRoute}/>;
                    }

                    if (isRedirect) {
                        redirectToTarget();
                        return <Loading {...props} />;
                    }

                    if (internalRedirect) {
                        return redirectObj();
                    }

                    return <Component {...props} />;
                }

                // Not authenticated
                // If showing dev token modal on localhost, show loading instead of redirecting
                if (showLocalTokenModal && authService.isLocalhost()) {
                    return <Loading />;
                }

                return (
                    <Redirect
                        to={{
                            pathname: '/auth',
                            state: {from: props.location}
                        }}
                    />
                );
            }}
            />
        </>
    );
};

export default ProtectedRoute;

